import NoDataIcon from '@/modules/Statistics/assets/icons/NoData.svg?react';
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type ScriptableLineSegmentContext,
} from 'chart.js';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { useSmoothScrollToCurrentMonth } from '../../hooks/useSmoothScrollToCurrentMonth';
import { LineChartProps } from './Chart.type';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineController,
  PointElement,
  LineElement,
  Tooltip,
);

const ITEM_PX = 60;
const CHART_HEIGHT = 280;
const MIN_WIDTH = 600;
const FONT_FAMILY = "'iran-yekan', sans-serif";

const Y_AXIS_MIN_WIDTH = 32;
const Y_AXIS_MAX_WIDTH = 64;
const Y_AXIS_CHAR_WIDTH = 12;
const Y_AXIS_PADDING = 8;

const SCROLLBAR_HEIGHT_FALLBACK = 16;
const SCROLL_GAP = 8;

const withFirstRankTick = (axis: { ticks: { value: number }[] }) => {
  const tickValues = axis.ticks.map((tick) => tick.value);

  if (!tickValues.includes(1)) tickValues.push(1);

  return tickValues
    .sort((a, b) => Number(a) - Number(b))
    .map((value) => ({ value }));
};

const X_TICKS = {
  minRotation: 0,
  maxRotation: 0,
  padding: 10,
  font: { size: 12, family: FONT_FAMILY },
} as const;

const Y_TICKS = {
  precision: 0,
  maxTicksLimit: 8,
  font: { size: 12, family: FONT_FAMILY },
} as const;

export default function LineChart({ labels, values, variant }: LineChartProps) {
  const mainColor = variant === 'country' ? '#8E68F6' : '#5FC1EB';

  const [scrollbarThickness, setScrollbarThickness] = useState(
    SCROLLBAR_HEIGHT_FALLBACK,
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const lineValues = useMemo(
    () => values.map((value) => (Number(value) > 0 ? value : null)),
    [values],
  );

  const hasValidData = useMemo(() => {
    return values.some((value) => Number(value) > 0);
  }, [values]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    const sync = () => {
      setScrollbarThickness(element.offsetHeight - element.clientHeight);
    };

    sync();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(sync);
    observer.observe(element);

    return () => observer.disconnect();
  }, [hasValidData]);

  const maxRankValue = useMemo(() => {
    const numericValues = values
      .map(Number)
      .filter((v) => Number.isFinite(v) && v > 0);
    return numericValues.length ? Math.max(...numericValues) : 1;
  }, [values]);

  const yAxisWidth = useMemo(() => {
    const chars = maxRankValue.toLocaleString('fa-IR').length;
    return Math.min(
      Math.max(chars * Y_AXIS_CHAR_WIDTH + Y_AXIS_PADDING, Y_AXIS_MIN_WIDTH),
      Y_AXIS_MAX_WIDTH,
    );
  }, [maxRankValue]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'رتبه',
          data: lineValues,
          borderColor: mainColor,
          borderWidth: 2,
          clip: 12,
          tension: 0,
          fill: false,
          segment: {
            borderColor: (ctx: ScriptableLineSegmentContext) => {
              const p0 = ctx.p0.parsed.y;
              const p1 = ctx.p1.parsed.y;
              if (p0 == null || p1 == null) return mainColor;
              return p1 < p0 ? '#0DC277' : '#E34C4C';
            },
          },
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#fff',
          pointBorderColor: mainColor,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: mainColor,
          pointHoverBorderWidth: 3,
        },
      ],
    }),
    [labels, lineValues, mainColor],
  );

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 12, bottom: 6, left: 0, right: 8 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          rtl: true,
          textDirection: 'rtl',
          backgroundColor: '#fff',
          bodyColor: '#646464',
          displayColors: true,
          usePointStyle: true,
          boxPadding: 8,
          padding: 10,
          position: 'nearest',
          yAlign: 'bottom',
          xAlign: 'center',
          caretSize: 0,
          caretPadding: 15,
          bodyFont: { size: 14, family: FONT_FAMILY },
          callbacks: {
            title: () => '',
            label: (ctx) =>
              `رتبه: ${Math.round(ctx.parsed?.y ?? 0).toLocaleString('fa-IR')}`,
            labelColor: () => ({
              borderColor: 'transparent',
              backgroundColor: mainColor,
              borderWidth: 0,
              borderRadius: 999,
            }),
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.05)',
            drawTicks: false,
          },
          ticks: {
            ...X_TICKS,
            color: '#787878',
          },
        },
        y: {
          reverse: true,
          min: 1,
          suggestedMax: maxRankValue,
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.05)',
            drawTicks: false,
          },
          border: { display: false },
          afterBuildTicks: withFirstRankTick,
          ticks: {
            ...Y_TICKS,
            display: false,
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = 0;
          },
        },
      },
    }),
    [mainColor, maxRankValue],
  );

  const axisData = useMemo<ChartData<'line'>>(
    () => ({
      labels: [''],
      datasets: [
        {
          data: [maxRankValue],
          borderColor: 'transparent',
          pointRadius: 0,
          fill: false,
        },
      ],
    }),
    [maxRankValue],
  );

  const axisOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      events: [],
      layout: {
        padding: { top: 12, bottom: 6, left: 0, right: 2 },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          grid: { display: false, drawTicks: false },
          ticks: {
            ...X_TICKS,
            color: 'transparent',
          },
        },
        y: {
          position: 'left',
          reverse: true,
          min: 1,
          suggestedMax: maxRankValue,
          grid: { display: false },
          border: { display: false },
          afterBuildTicks: withFirstRankTick,
          ticks: {
            ...Y_TICKS,
            color: '#787878',
            align: 'center',
            crossAlign: 'far',
            padding: 4,
            callback: (v) => `\u200E${Number(v).toLocaleString('fa-IR')}`,
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = yAxisWidth;
          },
        },
      },
    }),
    [yAxisWidth, maxRankValue],
  );

  const chartWidth = Math.max(labels.length * ITEM_PX, MIN_WIDTH);

  useSmoothScrollToCurrentMonth(scrollRef, labels, ITEM_PX);

  if (!hasValidData) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: CHART_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px',
          color: '#9CA3AF',
          textAlign: 'center',
        }}
      >
        <NoDataIcon />
        <span
          style={{
            fontSize: '14px',
            fontFamily: FONT_FAMILY,
            color: '#787878',
          }}
        >
          اطلاعاتی موجود نیست.
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        direction: 'ltr',
      }}
    >
      <div
        style={{
          flex: `0 0 ${yAxisWidth}px`,
          width: yAxisWidth,
          height: CHART_HEIGHT,
        }}
      >
        <Chart type="line" data={axisData} options={axisOptions} />
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: '1 1 0',
          minWidth: 0,
          height: CHART_HEIGHT + scrollbarThickness + SCROLL_GAP,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          style={{
            width: chartWidth,
            minWidth: '100%',
            height: CHART_HEIGHT,
          }}
        >
          <Chart type="line" data={data} options={options} />
        </div>
      </div>
    </div>
  );
}
